import React, { useMemo, useEffect, useState } from "react";
import { SideMenu } from "./SideMenu.js";
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  IconButton,
  Container,
  Tooltip,
  Icon,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { getPageRoute, hasValidConsumerSubscription } from "src/services";
import StanzaLogo from "src/assets/svg/stanza-logo.js";
import StanzaSmallLogo from "src/assets/svg/stanza-small-icon.js";
import { MenuOutlined, PersonOutlineOutlined, Add } from "@mui/icons-material";
import { useDimensions } from "src/utils/useDimensions.js";
import { useTheme } from "@emotion/react";
import { throttle } from "lodash";
import { TopBanner } from "../upgrades/TopBanner.js";
import { useTopNavbar } from "src/utils/TopBannerContext.js";

function Navbar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const AppBarRef = React.useRef(null);
  const TopBannerRef = React.useRef(null);
  const { showTopNavbar, topNavbarContent } = useTopNavbar();
  // const [openSignInDialog, setOpenSignInDialog] = useState(true);

  const loggedIn = useMemo(() => {
    return !!user;
  }, [user]);

  const [openSideMenu, setOpenSideMenu] = useState(false);
  const handleOpenSideMenu = () => setOpenSideMenu(true);
  const handleCloseSideMenu = () => setOpenSideMenu(false);

  const isOnUpgradePage = useMemo(() => {
    return location.pathname.startsWith("/upgrade");
  }, [location.pathname]);

  const { isMediumScreen } = useDimensions();

  useEffect(() => {
    const tabs = document.querySelector(".MuiTabs-root");
    const chatInterface = document.getElementById("chat-interface");
    const isInSearchPage = window.location.pathname === "/search";
    const target = isInSearchPage ? "search" : "window";
    let capturedScrollY_A = {
      window: 0,
      search: 0,
    }[target];
    let capturedScrollableHeight = {
      window: document.scrollingElement.scrollHeight,
      search: chatInterface?.scrollHeight,
    }[target];
    let offset = TopBannerRef.current ? TopBannerRef.current.clientHeight : 0;
    AppBarRef.current.style.top = `${offset}px`;
    if (AppBarRef.current?.nextElementSibling) {
      AppBarRef.current.nextElementSibling.style.marginTop = `${offset}px`;
    }

    const resizeObserver = new ResizeObserver(() => {
      offset = TopBannerRef.current ? TopBannerRef.current.clientHeight : 0;
      if (AppBarRef.current) {
        AppBarRef.current.style.top = `${offset}px`;
      }
      if (AppBarRef.current?.nextElementSibling) {
        AppBarRef.current.nextElementSibling.style.marginTop = `${offset}px`;
      }
    });

    // save reference for don't lost it at useEffect cleanup
    const bannerElement = TopBannerRef.current;
    if (bannerElement) {
      resizeObserver.observe(bannerElement);
    }

    const handleScroll = (
      isTabStuck,
      targetElement,
      { onScrollUp, onScrollDown }
    ) => {
      if (capturedScrollableHeight !== targetElement.scrollHeight) {
        capturedScrollY_A = targetElement.scrollTop;
        capturedScrollableHeight = targetElement.scrollHeight;
        return;
      }
      // if at top of page, show navbar
      if (targetElement.scrollTop === 0) {
        return onScrollUp();
      }
      // if at bottom of page, don't show navbar
      if (
        targetElement.scrollHeight - targetElement.scrollTop ===
        targetElement.clientHeight
      ) {
        return onScrollDown();
      }
      if (!isTabStuck) return onScrollUp();
      const capturedScrollY_B = targetElement.scrollTop;
      const capturedScrollDiff = capturedScrollY_B - capturedScrollY_A;

      if (capturedScrollDiff < -30 && isTabStuck) {
        // if scrolling up and tab is stuck
        onScrollUp();
      } else if (capturedScrollDiff > 30 && isTabStuck) {
        // if scrolling down and tab is stuck
        onScrollDown();
      }

      capturedScrollY_A = targetElement.scrollTop; // reset last scroll position
      capturedScrollableHeight = targetElement.scrollHeight; // reset scrollable height
    };

    // For window scroll
    const handleScrollWindow = () => {
      if (!AppBarRef.current || !tabs) return;
      document.scrollingElement.style.overscrollBehavior = "none";
      const clientBounding = tabs?.getBoundingClientRect();
      const isTabStuck = clientBounding?.top < 70 + offset;
      const onScrollUp = () => {
        if (AppBarRef.current.style.opacity === "1") return;
        AppBarRef.current.style.opacity = "1";
        tabs.style.top = `${48 + offset}px`;
      };
      const onScrollDown = () => {
        if (AppBarRef.current.style.opacity === "0") return;
        AppBarRef.current.style.opacity = "0";
        tabs.style.top = `${0 + offset}px`;
      };
      handleScroll(isTabStuck, document.scrollingElement, {
        onScrollUp,
        onScrollDown,
      });
    };

    // For search page scroll
    const handleScrollSearch = () => {
      if (!AppBarRef.current || !tabs || !chatInterface) return;
      chatInterface.style.overscrollBehavior = "none";
      const onScrollUp = () => {
        if (AppBarRef.current.style.opacity === "1") return;
        AppBarRef.current.style.opacity = "1";
        AppBarRef.current.style.height = "56px";
        if (!chatInterface) {
          tabs.style.top = `${48 + offset}px`;
        } else {
          tabs.style.top = `${0 + offset}px`;
        }
      };
      const onScrollDown = () => {
        if (AppBarRef.current.style.opacity === "0") return;
        AppBarRef.current.style.opacity = "0";
        AppBarRef.current.style.height = "0px";
        tabs.style.top = `${0 + offset}px`;
      };
      handleScroll(true, chatInterface, { onScrollUp, onScrollDown });
    };

    const functionToCall = {
      window: handleScrollWindow,
      search: handleScrollSearch,
    }[target];

    // Throttle scroll handler to improve performance
    const throttledHandleScroll = throttle(functionToCall, 100);

    if (!isInSearchPage) {
      window.addEventListener("scroll", throttledHandleScroll);
    } else if (isInSearchPage) {
      chatInterface?.addEventListener("scroll", throttledHandleScroll);
    }

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      chatInterface?.removeEventListener("scroll", throttledHandleScroll);
      if (bannerElement) {
        resizeObserver.unobserve(bannerElement);
      }
    };
  }, [showTopNavbar]);

  const StanzaLogoSelected = loggedIn
    ? isMediumScreen
      ? StanzaSmallLogo
      : StanzaLogo
    : StanzaLogo;

  return (
    <>
      {showTopNavbar && (
        <TopBanner id="TopBanner" ref={TopBannerRef}>
          {topNavbarContent}
        </TopBanner>
      )}
      <AppBar
        ref={AppBarRef}
        id="AppBar"
        position="sticky"
        sx={{
          backgroundColor: "white",
          zIndex: "4",
          transition: "opacity, height 0.1s linear",
          boxShadow: "none !important",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              margin: !isMediumScreen && "0rem 3rem",
              minHeight: { md: "48px" },
              py: { xs: 0, md: 0.5 },
            }}
          >
            <StanzaLogoSelected
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/")}
            />

            <Box sx={{ flexGrow: 1 }}></Box>

            {loggedIn ? (
              <>
                {!hasValidConsumerSubscription(user) && !isOnUpgradePage && (
                  <Button
                    variant="outlined"
                    size="shrinked"
                    onClick={() =>
                      navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                        state: { source: "nav_button" },
                      })
                    }
                    sx={{
                      display: "inline",
                      color: "black",
                      ml: 1,
                      mr: { xs: 1, sm: 2 },
                      borderColor: "black",
                      "&:hover": {
                        borderColor: "black",
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    Go Plus
                  </Button>
                )}
              </>
            ) : (
              !isOnUpgradePage && (
                <Button
                  variant="outlined"
                  size="shrinked"
                  onClick={() =>
                    navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                      state: { source: "nav_button_signed_out" },
                    })
                  }
                  sx={{
                    color: "black",
                    display: "block",
                    mr: 1,
                    borderColor: "black",
                    "&:hover": {
                      borderColor: "black",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  Go Plus
                </Button>
              )
            )}
            <Tooltip title="Search">
              <IconButton
                onClick={() => navigate("/search")}
                color="primary"
                size="small"
                sx={{ mr: 1 }}
              >
                <Icon
                  baseClassName="material-symbols-outlined"
                  fontSize="medium"
                  sx={{
                    color: `${theme.palette.icon.primary}`,
                    verticalAlign: "middle",
                  }}
                >
                  search
                </Icon>
              </IconButton>
            </Tooltip>

            {loggedIn ? (
              <Box
                display="flex"
                alignItems={isMediumScreen ? "end" : "center"}
                sx={{ flexGrow: 0 }}
              >
                <Button
                  data-testid="home-user-import"
                  variant="filled"
                  component={RouterLink}
                  to={getPageRoute("help", "HELP_IMPORT")}
                  sx={{
                    display: { xs: "none", md: "inline" },
                    color: `${theme.palette.text.primary}`,
                    px: { md: "8px" },
                  }}
                >
                  Import
                </Button>
                <Tooltip title="Import">
                  <IconButton
                    data-testid="home-user-import"
                    onClick={() =>
                      navigate(getPageRoute("help", "HELP_IMPORT"))
                    }
                    color="primary"
                    size="small"
                    sx={{ display: { xs: "inline-flex", md: "none" }, mr: 1 }}
                  >
                    <Add
                      fontSize="medium"
                      sx={{ color: `${theme.palette.icon.primary}` }}
                    />
                  </IconButton>
                </Tooltip>

                <Button
                  data-testid="home-user-library"
                  variant="filled"
                  component={RouterLink}
                  to="/library"
                  sx={{
                    display: { xs: "none", md: "inline" },
                    color: `${theme.palette.text.primary}`,
                    px: { md: "8px" },
                    mr: { md: "2px" },
                  }}
                >
                  Library
                </Button>

                <Tooltip title="Your Library">
                  <IconButton
                    onClick={() => navigate("/library")}
                    color="primary"
                    size="small"
                    sx={{ display: { xs: "inline-flex", md: "none" }, mr: 1 }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      fontSize="medium"
                      sx={{
                        color: `${theme.palette.icon.primary}`,
                        verticalAlign: "middle",
                      }}
                    >
                      newsstand
                    </Icon>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Open Settings">
                  <IconButton
                    data-testid="home-user-menu"
                    onClick={handleOpenSideMenu}
                    size="small"
                  >
                    <PersonOutlineOutlined
                      fontSize="medium"
                      sx={{ color: `${theme.palette.icon.primary}` }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to="/signin"
                  variant="text"
                  sx={{
                    display: { xs: "none", md: "inline" },
                    color: "text.primary",
                    mr: 1,
                  }}
                >
                  Sign In
                </Button>

                <Tooltip title="Sign In">
                  <IconButton
                    component={RouterLink}
                    to="/signin"
                    size="small"
                    sx={{ display: { xs: "flex", md: "none" }, mr: 1 }}
                  >
                    <PersonOutlineOutlined
                      fontSize="medium"
                      sx={{
                        color: `${theme.palette.icon.primary}`,
                        gap: "8px",
                      }}
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Open Menu">
                  <IconButton onClick={handleOpenSideMenu} size="small">
                    <MenuOutlined
                      fontSize="medium"
                      sx={{ color: `${theme.palette.icon.primary}` }}
                    />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <SideMenu open={openSideMenu} onClose={handleCloseSideMenu} user={user} />

      {/*
      {!user && (
        <SignInDialog
          open={openSignInDialog}
          handleClose={() => setOpenSignInDialog(false)}
          openOnScroll={true}
        />
      )}
        */}
    </>
  );
}
export default Navbar;
